import React, { ReactNode, useEffect, useState } from 'react';
import './allowed-domain-list.css';
import { IActionProps, Toolbar } from '../../ui/Toolbar';
import { DataGrid } from '../../ui/DataGrid';
import { AllowedDomain, allowedDomainApi, AllowedDomainListItem } from '../../../shared/services/adminApi';
import { IConfirmProps } from '../../ui/ConfirmationBox';
import { DetailFormWrapper, DetailsForm } from '../../ui/DetailForm';
import { IApplicationModule } from '../../ui/IApplicationModule';
import { AddNewButtonText, EnableOrDisableButtonText, ViewOrEditButtonText } from './general-administration-data';
import { IModalBoxProps } from '../../ui/ModalBox';
import { IUserSearchResult } from '../../../shared/models/api/models';

interface IAllowedDomainListProps {
    module: IApplicationModule,
    loadingState: string,
    setLoadingState: (newState: string) => void,
    setConfirmProps: (confirmProps: IConfirmProps | null) => void,
    setModalProps: (modalProps: IModalBoxProps | null) => void
}

export const AllowedDomainList = (props: IAllowedDomainListProps) => {
    const [domains, setDomains] = useState([] as AllowedDomainListItem[]);
    const [selectedDomains, setSelectedDomains] = useState([] as AllowedDomainListItem[]);

    const getAllowedDomains = async () => {
        await allowedDomainApi.listAsync()
            .then(result => {
                if (result) {
                    setDomains(result);
                }
            });
    };

    const onGridRowClick = (row: AllowedDomain) => {
        var arr = selectedDomains.filter(r => r.id !== row.id);

        if (arr.length === selectedDomains.length) {
            arr.push(row);
        }

        setSelectedDomains(arr);
    };

    const canClose = () => {
        return true;
    }

    const ask = (
        msg: string,
        opts: string[],
        def: string,
        defFn: () => void,
        otherFn?: (result: string) => void
    ) => {
        props.setConfirmProps({
            message: msg,
            options: opts,
            defaultOption: def,
            setResult: result => {
                // close the confirm box
                if (props.setConfirmProps) {
                    props.setConfirmProps(null);
                }

                if (result === def) {
                    if (defFn) defFn();
                } else {
                    if (otherFn) otherFn(result);
                }
            }
        });
    };

    const createBlankDomain = () => {
        return {
            contractPartner: "",
            sector: "",
            companyWebsite: "",
            emailDomain: ""
        } as AllowedDomain;
    };

    const onDataChanged = (domain: AllowedDomain, msg: string | undefined) => {
        const idx = domains.findIndex(d => d.id === domain.id);

        if (idx > -1) {
            // take a copy of the array
            var arr = domains.map(u => u);

            // replace the row with the updated domain
            arr[idx] = domain;

            // set the new array back to the all domains
            setDomains(arr);
        } else {
            // just get all users again
            getAllowedDomains();
        }

        props.setModalProps(null);
        msgBox(msg ?? "Your request has been successfully completed.");
    };

    const addNew = (icon: any) => {
        props.setModalProps({
            className: "allowed-domain-modal",
            body:
                <DetailFormWrapper<AllowedDomain>
                    create={createBlankDomain}
                    save={allowedDomainApi.addAsync}
                    viewName="new"
                    onDataChanged={onDataChanged}
                    module={props.module}
                    setConfirmationProps={props.setConfirmProps}
                    closeDialog={() => props.setModalProps(null)}
                />,
            setResult: result => {
                switch (result) {
                    case "Close":
                    case "CloseModal":
                    case "ClickOutsideModal":
                        props.setModalProps(null);
                        break;
                }
            }
        });
    };

    const viewOrEdit = (row: AllowedDomainListItem) => {
        if (row) {
            props.setModalProps({
                className: "allowed-domain-modal",
                body:
                    <DetailFormWrapper<AllowedDomain>
                        load={() => allowedDomainApi.getAsync(row.id)}
                        save={allowedDomainApi.updateAsync}
                        viewName="view"
                        onDataChanged={onDataChanged}
                        module={props.module}
                        setConfirmationProps={props.setConfirmProps}
                        closeDialog={() => props.setModalProps(null)}
                    />
            });
        }
    };

    const msgBox = (msg: ReactNode) => {
        if (props.setConfirmProps) {
            props.setConfirmProps({
                message: msg,
                options: ["OK"],
                defaultOption: "OK",
                setResult: result => {
                    if (props.setConfirmProps) {
                        props.setConfirmProps(null);
                    }
                }
            });
        }
    };

    const confirmEnableOrDisable = (allowedDomains: AllowedDomainListItem[]) => {
        const theseDomains = allowedDomains.length === 1 ? "this domain" : "these domains";
        const are = allowedDomains.length === 1 ? "is" : "are";
        const allUsers = allowedDomains.length === 1 ? "All users" : "Users"

        const DISABLED = false;

        // assume the state from the first selected item
        const currentState = allowedDomains[0].isEnabled;
        const newState = !currentState;

        const enableSlashDisable = newState === DISABLED ? "disable" : "enable";
        const proceedButtonText = newState === DISABLED ? "Disable" : "Enable";
        const enabledSlashDisabled = enableSlashDisable + "d";
        const activeSlashInactivePhrase = newState === DISABLED ? "made inactive and lose their access to" : "able to access";

        // check if there is a mixture of IsEnabled values in the selected values
        if (allowedDomains.filter(d => d.isEnabled === newState).length > 0) {
            msgBox("Some of " + { theseDomains } + " are already " + enabledSlashDisabled + ".");
            return;
        }

        if (props.setConfirmProps) {
            props.setConfirmProps({
                message: <div>Are you sure you want to {enableSlashDisable} {theseDomains}? {allUsers} associated with {theseDomains} will be {activeSlashInactivePhrase} the BHP Rail App.</div>,
                options: ["Cancel", proceedButtonText],
                defaultOption: proceedButtonText,
                setResult: result => {
                    if (result === "Cancel") {
                        props.setConfirmProps(null);
                        return;
                    }

                    if (result === proceedButtonText) {
                        allowedDomainApi.setIsEnabled(allowedDomains.map(allowedDomain => allowedDomain.id), newState)
                            .then(result => {
                                if (!!result) {
                                    //https://app.zeplin.io/project/5ffea29702627814f1355337/screen/6114f6c4c47ff193f8eb3000
                                    //msgBox(<div className="success-message">Success, {theseDomains} {are} now <span className="new-is-enabled-status">{enabledSlashDisabled}</span>.</div>);
                                    msgBox("Your request has been successfully completed.");
                                    // update the rows locally so we dont have to fetch them from the server
                                    allowedDomains.forEach(allowedDomain => { allowedDomain.isEnabled = newState; });
                                }
                            })
                            .catch(e => {
                                console.log(e);
                                msgBox(<div className="error-message">There was an error processing your request.</div>);
                            });

                        return;
                    }
                }
            });
        }
    };

    const onGridRowDoubleClick = (row: AllowedDomainListItem) => {
        viewOrEdit(row);
    };

    const checkMinMax = (action: IActionProps) => {
        if (selectedDomains.length === 0 && (action.minSelectedCount ?? 0) > 0) {
            msgBox("Please select one item to continue.");
            return false;
        }

        if (!!action.maxSelectedCount) {
            if (selectedDomains.length > action.maxSelectedCount) {
                if (action.maxSelectedCount === 1) {
                    msgBox("Please select only one item to continue.");
                } else {
                    msgBox("Please select " + action.maxSelectedCount + " or fewer items to continue.");
                }

                return false;
            }
        }

        return true;
    };

    const actionClicked = (action: IActionProps) => {
        if (checkMinMax(action)) {
            switch (action.name) {
                case AddNewButtonText:
                    return addNew(action.icon);
                case ViewOrEditButtonText:
                    return viewOrEdit(selectedDomains[0]);
                case EnableOrDisableButtonText:
                    return confirmEnableOrDisable(selectedDomains);
                default:
                    console.warn("Unhandled action: " + action.name);
            }
        }
    };

    var loading = false;

    useEffect(() => {
        if (!loading) {
            loading = true;

            getAllowedDomains();
        }
    }, []);

    const compareInts = (a: number, b: number) => {
        if (a < b) return -1;
        if (a > b) return 1;
        return 0;
    };

    return (
        <div>
            <Toolbar
                actions={props.module?.actions ?? []}
                onClick={actionClicked}
                selectedCount={selectedDomains.length} />

            <DataGrid
                rows={domains}
                selectedItems={selectedDomains}
                setSelectedItems={setSelectedDomains}
                selectMode={props.module.selectMode}
                pageSize={props.module.pageSize ?? 10}
                columns={
                    (props.module.columns ?? [])
                        .filter(c => c.showInGrid)
                        .sort((a, b,) => compareInts(a.showInGrid ?? 0, b.showInGrid ?? 0))
                    ?? []
                }
                onDoubleClick={onGridRowDoubleClick} />
        </div>
    );
};

export default AllowedDomainList;